import { useCallback, useEffect, useState } from 'react';
import { authService, usuariosService } from '../../services/api';
import { useToast } from '../../components/ui/Toast';
import { btnPrimario, btnSecundario, chip } from '../../components/ui/estilos';
import { entrada, tarjetaLista } from '../agenda/campos';
import { DialogoContrasena, DialogoNuevoEmpleado, ROL_LABEL, useClaveAdmin } from './DialogosEquipo';
import DetalleEmpleado from './DetalleEmpleado';

const err = (e: any, def: string) => e?.response?.data?.error || def;

export default function Equipo() {
  const [toast, say] = useToast();
  const [usuarios, setUsuarios] = useState<any[]>([]);
  const [solicitudes, setSolicitudes] = useState<any[]>([]);
  const [busca, setBusca] = useState('');
  const [verInactivos, setVerInactivos] = useState(false);
  const [selId, setSelId] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState<null | 'nuevo' | 'clave'>(null);
  const [pedirClave, claveEl] = useClaveAdmin();

  const cargar = useCallback(() => usuariosService.getAll().then(r => setUsuarios(r.data)).catch(() => say('No se pudo cargar el equipo.')), [say]);
  const cargarSolicitudes = () => authService.getSolicitudesRecuperacion().then(r => setSolicitudes(r.data.filter((s: any) => !s.resuelta))).catch(() => setSolicitudes([]));
  useEffect(() => { cargar(); cargarSolicitudes(); }, [cargar]);

  const q = busca.trim().toLowerCase();
  const inactivos = usuarios.filter(u => !u.activo).length;
  const visibles = usuarios.filter(u => (verInactivos || u.activo) && `${u.nombre} ${u.email} ${u.rol}`.toLowerCase().includes(q));
  const sel = usuarios.find(u => u.id === selId) || null;

  // Cambios que se guardan solos (rol, permisos, activo): si el servidor los rechaza (p. ej. el último admin) se dice.
  const cambiar = async (u: any, datos: any, ok?: string) => {
    try { await usuariosService.actualizar(u.id, datos); await cargar(); if (ok) say(ok); }
    catch (e) { say(err(e, 'No se pudo guardar el cambio.')); }
  };

  const crear = async (datos: any): Promise<string | null> => {
    const clave = await pedirClave(`Crear la cuenta de ${datos.nombre}`);
    if (!clave) return null;
    try {
      const { data } = await usuariosService.crear({ ...datos, estacion: 'ambas', password_admin: clave });
      setDialogo(null); await cargar(); setSelId(data?.id || null); say(`${datos.nombre} agregado al equipo.`);
      return null;
    } catch (e) { return err(e, 'No se pudo crear la cuenta.'); }
  };

  const cambiarClave = async (password: string): Promise<string | null> => {
    if (!sel) return null;
    const clave = await pedirClave(`Cambiar la contraseña de ${sel.nombre}`);
    if (!clave) return null;
    try { await usuariosService.actualizar(sel.id, { password, password_admin: clave }); setDialogo(null); say('Contraseña cambiada.'); return null; }
    catch (e) { return err(e, 'No se pudo cambiar la contraseña.'); }
  };

  const eliminar = async (u: any) => {
    if (!window.confirm(`¿Borrar a ${u.nombre}?\n\nNo se puede deshacer. Ya no podrá entrar, pero su historial (comandas, cortes, movimientos) se conserva.\n\nLo normal es desactivarlo.`)) return;
    const clave = await pedirClave(`Borrar a ${u.nombre}`);
    if (!clave) return;
    try {
      const r = await usuariosService.eliminar(u.id, clave);
      setSelId(null); await cargar();
      say(r.data?.historial_conservado ? `${u.nombre} se borró; su historial se conservó.` : `${u.nombre} se borró por completo.`);
    } catch (e) { say(err(e, 'No se pudo borrar.')); }
  };

  return (
    <div className="paneles" style={{ margin: '-22px -24px', height: 'calc(100% + 44px)', minHeight: 480, display: 'grid', gridTemplateColumns: 'minmax(260px,1fr) clamp(320px,42%,500px)' }}>
      <section aria-label="Equipo" style={{ minHeight: 0, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nombre, correo o rol…" aria-label="Buscar empleado" style={{ ...entrada, flex: 1, minWidth: 200, width: 'auto', borderRadius: 12 }} />
          <button onClick={() => setDialogo('nuevo')} style={{ ...btnPrimario, height: 48, fontSize: 15 }}>+ Nuevo empleado</button>
        </div>
        {inactivos > 0 && (
          <button onClick={() => setVerInactivos(v => !v)} aria-pressed={verInactivos} style={{ ...btnSecundario, height: 40, fontSize: 14, alignSelf: 'flex-start', borderColor: verInactivos ? 'var(--ink)' : undefined }}>
            {verInactivos ? 'Ocultar' : 'Ver'} {inactivos} inactivo{inactivos > 1 ? 's' : ''}
          </button>
        )}
        {solicitudes.length > 0 && (
          <div role="status" style={{ padding: 14, borderRadius: 14, background: 'var(--warn-bg)', color: 'var(--warn)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <b>{solicitudes.length} solicitud{solicitudes.length > 1 ? 'es' : ''} de recuperación de contraseña</b>
            {solicitudes.map(s => (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--ink)' }}>{s.email} <span style={{ fontSize: 13, color: 'var(--muted)' }}>· {new Date(s.created_at).toLocaleString('es-MX')}</span></span>
                <button onClick={() => authService.resolverSolicitud(s.id).then(cargarSolicitudes)} style={{ ...btnSecundario, height: 36, fontSize: 13 }}>Marcar resuelta</button>
              </div>
            ))}
            <span style={{ fontSize: 13 }}>Elige al empleado, cámbiale la contraseña y marca la solicitud como resuelta.</span>
          </div>
        )}
        {!visibles.length && <div style={{ padding: 30, textAlign: 'center', color: 'var(--muted)' }}>No hay empleados con ese filtro.</div>}
        {visibles.map(u => (
          <button key={u.id} onClick={() => setSelId(u.id)} aria-pressed={selId === u.id} style={{ ...tarjetaLista(selId === u.id), flexDirection: 'row', alignItems: 'center', gap: 12, opacity: u.activo ? 1 : .6 }}>
            <span aria-hidden style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--well)', display: 'grid', placeItems: 'center', fontWeight: 700, flex: 'none' }}>
              {(u.nombre || 'U').split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <b style={{ fontSize: 16 }}>{u.nombre}</b>
              <span style={{ display: 'block', fontSize: 13, color: 'var(--muted)' }}>{ROL_LABEL[u.rol] || u.rol}{u.rol === 'cocina' && u.estacion && u.estacion !== 'ambas' ? ` · ${u.estacion}` : ''}</span>
            </span>
            <span style={{ ...chip(u.activo ? 'var(--ok-bg)' : 'var(--well)', u.activo ? 'var(--ok)' : 'var(--muted)'), fontSize: 12 }}>{u.activo ? 'Activo' : 'Inactivo'}</span>
          </button>
        ))}
      </section>
      <aside aria-label="Detalle del empleado" style={{ borderLeft: '1px solid var(--line)', background: 'var(--surface)', minHeight: 0, overflowY: 'auto' }}>
        {sel
          ? <DetalleEmpleado key={sel.id} u={sel} onCambiar={(d, ok) => cambiar(sel, d, ok)} onClave={() => setDialogo('clave')} onEliminar={() => eliminar(sel)} />
          : <div style={{ height: '100%', display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 16 }}>Elige un empleado para ver su detalle.</div>}
      </aside>
      {dialogo === 'nuevo' && <DialogoNuevoEmpleado onCerrar={() => setDialogo(null)} onCrear={crear} />}
      {dialogo === 'clave' && sel && <DialogoContrasena nombre={sel.nombre} onCerrar={() => setDialogo(null)} onGuardar={cambiarClave} />}
      {claveEl}
      {toast}
    </div>
  );
}
